import { PagePaginationFromKeys, SortDirectionCapital } from '../../types'

export enum PlannedTradeDirection {
  long = 'long',
  short = 'short',
}

export enum PlannedTradeOutcome {
  successful = 'successful',
  unsuccessful = 'unsuccessful',
  breakeven = 'breakeven',
  missed = 'missed',
}

export interface PlannedTrade {
  id: number
  plannedDate: string // ISO datetime string (з часом)
  symbol: string
  direction: PlannedTradeDirection // завжди обов’язкове
  technicalConfluences: string // JSON або CSV
  fundamentalOutlook: string
  entryPrice: number | null
  stopLoss: number | null
  takeProfit: number | null
  positionSize: number | null
  contractSize: number | null
  accountId: number
  accountName: string
  riskPercent: number | null
  riskRewardRatio: number | null
  tradeTriggerDate: string | null // ISO datetime string
  tradeCloseDate: string | null // ISO datetime string
  screenshotBeforeUrl: string | null // завжди є ключ, навіть якщо null
  screenshotAfterUrl: string | null
  outcome: PlannedTradeOutcome // завжди обов’язкове
  missedReason: string | null
  missedWasSuccessful: boolean | null
  pnl: number | null
  deviated: boolean | null
  notes: string | null
  createdAt: string // ISO datetime string
  updatedAt: string
}

export type PlannedTradeColumnKey =
  | 'symbol'
  | 'plannedDate'
  | 'direction'
  | 'entryPrice'
  | 'stopLoss'
  | 'takeProfit'
  | 'positionSize'
  // | 'contractSize'
  | 'riskPercent'
  | 'riskRewardRatio'
  | 'tradeTriggerDate'
  | 'tradeCloseDate'
  | 'outcome'
  | 'pnl'
  | 'deviated'

export interface TradingPlanRequestParams {
  page: number
  perPage: number
  order: SortDirectionCapital
  orderBy: PlannedTradeColumnKey | null
  accountId?: number
  search?: string
  columns?: PlannedTradeColumnKey[]
}

export interface PlannedTradeResponse {
  data: PlannedTrade[]
  meta: PagePaginationFromKeys<PlannedTradeColumnKey>
}

export interface TradingPlanInitialState {
  plannedTrades: PlannedTrade[]
  pagination: PagePaginationFromKeys<PlannedTradeColumnKey>
  loading: boolean
  saving: boolean
  deleting: boolean
  reports: TradingPlanReportsResponse | null
  reportsLoading: boolean
  reportsError: string | null
}

export interface PlannedTradeFormValues {
  id?: number
  plannedDate: string
  symbol: string
  direction: PlannedTradeDirection | null
  technicalConfluences: string
  tradingConfluence: Record<string, boolean>
  customTradingConfluence: Record<string, boolean>
  fundamentalOutlook: string
  entryPrice: string
  stopLoss: string
  takeProfit: string
  positionSize: string
  contractSize: string
  tradeTriggerDate: string
  tradeCloseDate: string
  screenshotBefore: File | null
  removeScreenshotBefore: boolean
  screenshotAfter: File | null
  removeScreenshotAfter: boolean
  outcome: PlannedTradeOutcome | null
  missedReason: string
  missedWasSuccessful: boolean | null
  pnl: string
  deviated: boolean | null
  notes: string
}

export interface PlannedTradeMutationPayload {
  values: PlannedTradeFormValues
  accountId: number
  riskPercent: number | null
  riskRewardRatio: number | null
}

export type PlannedTradeOutcomeWithUnknown = PlannedTradeOutcome | 'unknown'

export interface TradingPlanReportRequest {
  accountId: number
  period?: (string | null)[]
}

export interface TradingPlanOutcomeBreakdown {
  outcome: PlannedTradeOutcomeWithUnknown
  count: number
  percentage: number
}

export interface TradingPlanPnlBySymbol {
  symbol: string
  pnl: number
}

export interface TradingPlanRiskRewardByOutcome {
  outcome: PlannedTradeOutcomeWithUnknown
  average: number | null
  tradeCount: number
}

export interface TradingPlanDeviationSummary {
  deviated: number
  notDeviated: number
}

export interface TradingPlanConfluenceFrequency {
  name: string
  count: number
}

export interface TradingPlanConfluenceProfitability {
  name: string
  pnl: number
}

export interface TradingPlanReportsResponse {
  meta: {
    accountId: number
    period: [string | null, string | null]
    totalTrades: number
  }
  outcomeOverview: {
    total: number
    breakdown: TradingPlanOutcomeBreakdown[]
  }
  totalPnl: {
    total: number
    bySymbol: TradingPlanPnlBySymbol[]
  }
  riskRewardByOutcome: TradingPlanRiskRewardByOutcome[]
  deviation: TradingPlanDeviationSummary
  confluences: {
    mostPopular: TradingPlanConfluenceFrequency[]
    mostProfitable: TradingPlanConfluenceProfitability[]
  }
}
